import type { Middleware } from "@reduxjs/toolkit";
import isEqual from "lodash/isEqual";
import type { ProposalStoreState } from "work/proposals/_store/proposals-redux-slice";

export const PROPOSAL_LOCAL_STORE_STORAGE_KEY = "attorneyhub.proposals.localStore.v1";

type StoredProposalLocalStoreState = {
  proposals: ProposalStoreState;
  version: 1;
};

type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;

export function extractProposalLocalStoreState(state: ProposalStoreState): ProposalStoreState {
  const byId: ProposalStoreState["byId"] = {};

  for (const [proposalIdValue, proposalEntry] of Object.entries(state.byId)) {
    if (!proposalEntry) continue;

    const localEntry = extractProposalLocalStoreEntry(proposalEntry);
    if (localEntryHasLocalState(localEntry)) {
      byId[proposalIdValue] = localEntry;
    }
  }

  return {
    byId,
    byQuery: {},
  };
}

export function loadProposalLocalStoreState(storage = getBrowserLocalStorage()): ProposalStoreState | undefined {
  if (!storage) return undefined;

  try {
    const serializedState = storage.getItem(PROPOSAL_LOCAL_STORE_STORAGE_KEY);
    if (!serializedState) return undefined;

    const parsedState = JSON.parse(serializedState) as unknown;
    return parseStoredProposalLocalStoreState(parsedState);
  } catch {
    return undefined;
  }
}

export function saveProposalLocalStoreState(
  state: ProposalStoreState,
  storage = getBrowserLocalStorage(),
): void {
  if (!storage) return;

  const localStoreState = extractPersistableProposalLocalStoreState(state);

  try {
    if (Object.keys(localStoreState.byId).length === 0) {
      storage.removeItem(PROPOSAL_LOCAL_STORE_STORAGE_KEY);
      return;
    }

    const storedState: StoredProposalLocalStoreState = {
      proposals: localStoreState,
      version: 1,
    };
    storage.setItem(PROPOSAL_LOCAL_STORE_STORAGE_KEY, JSON.stringify(storedState));
  } catch {
    return;
  }
}

export const proposalLocalStorePersistenceMiddleware: Middleware = ({ getState }) => (next) => (action) => {
  const previousProposalState = getProposalStateFromRootState(getState());
  const result = next(action);
  const nextProposalState = getProposalStateFromRootState(getState());

  if (nextProposalState && nextProposalState !== previousProposalState) {
    saveProposalLocalStoreState(nextProposalState);
  }

  return result;
};

function parseStoredProposalLocalStoreState(value: unknown): ProposalStoreState | undefined {
  if (!isRecord(value) || value.version !== 1 || !isProposalStoreState(value.proposals)) {
    return undefined;
  }

  return extractProposalLocalStoreState(value.proposals);
}

function extractProposalLocalStoreEntry(
  proposalEntry: NonNullable<ProposalStoreState["byId"][string]>,
): NonNullable<ProposalStoreState["byId"][string]> {
  const localEntry: NonNullable<ProposalStoreState["byId"][string]> = {};
  const draftBuilder = extractProposalDraftBuilderEntry(proposalEntry.draftBuilder);

  if (draftBuilder) localEntry.draftBuilder = draftBuilder;
  if (proposalEntry.localRevision) localEntry.localRevision = proposalEntry.localRevision;
  if (proposalEntry.localVersion) localEntry.localVersion = proposalEntry.localVersion;

  return localEntry;
}

function extractProposalDraftBuilderEntry(
  draftBuilder: NonNullable<ProposalStoreState["byId"][string]>["draftBuilder"],
): NonNullable<ProposalStoreState["byId"][string]>["draftBuilder"] {
  if (!draftBuilder?.original && !draftBuilder?.updated) return undefined;

  return {
    original: draftBuilder.original,
    updated: draftBuilder.updated,
  };
}

/**
 * The local state worth writing to browser storage: what the extraction above keeps, less the
 * cached builders of every draft the server already holds unchanged.
 *
 * This cache exists for an accidental refresh, so it keeps a builder only while the browser is the
 * only place that builder lives. A save settles that — `promoteCreatedProposalDraft` puts both
 * snapshots on the response the server has just persisted, so the pair records the saved baseline
 * and no edit at all — and a submit settles it the same way. Writing that pair left every draft the
 * actor had ever saved in browser storage, and left the new-draft slot holding a builder the next
 * proposal creation then opened on, with the previous draft's fields already in it (#5667).
 *
 * Only browser storage drops the pair. Redux keeps it for the rest of the session, which is what
 * the editor reads while a created draft's own route hydrates. The filter also belongs here rather
 * than in the extraction above, which `hydrateLocalProposalStore` reads a stored payload through:
 * a stored payload carries no server version to compare a builder against.
 */
function extractPersistableProposalLocalStoreState(state: ProposalStoreState): ProposalStoreState {
  const byId: ProposalStoreState["byId"] = {};

  for (const [proposalIdValue, localEntry] of Object.entries(extractProposalLocalStoreState(state).byId)) {
    if (!localEntry) continue;

    if (!localEntry.draftBuilder || !draftBuilderIsTheSavedBaseline(state.byId[proposalIdValue])) {
      byId[proposalIdValue] = localEntry;
      continue;
    }

    const remainingEntry: NonNullable<ProposalStoreState["byId"][string]> = {};
    if (localEntry.localRevision) remainingEntry.localRevision = localEntry.localRevision;
    if (localEntry.localVersion) remainingEntry.localVersion = localEntry.localVersion;
    if (localEntryHasLocalState(remainingEntry)) byId[proposalIdValue] = remainingEntry;
  }

  return {
    byId,
    byQuery: {},
  };
}

/** Whether this draft's two cached builders agree on a spec the server already holds. */
function draftBuilderIsTheSavedBaseline(proposalEntry: ProposalStoreState["byId"][string]): boolean {
  if (!proposalEntry?.serverVersion) return false;
  return isEqual(proposalEntry.draftBuilder?.original, proposalEntry.draftBuilder?.updated);
}

function localEntryHasLocalState(proposalEntry: NonNullable<ProposalStoreState["byId"][string]>): boolean {
  return Boolean(proposalEntry.localRevision || proposalEntry.localVersion || proposalEntry.draftBuilder);
}

function getProposalStateFromRootState(rootState: unknown): ProposalStoreState | undefined {
  if (!isRecord(rootState) || !isProposalStoreState(rootState.proposals)) return undefined;
  return rootState.proposals;
}

function isProposalStoreState(value: unknown): value is ProposalStoreState {
  return isRecord(value) && isRecord(value.byId) && isRecord(value.byQuery);
}

function getBrowserLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
